import React, { useCallback, useMemo, useState } from "react";
import {
  Autocomplete,
  Avatar,
  AvatarGroup,
  Box,
  Chip,
  Paper,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  PictureAsPdf as PdfIcon,
  InsertDriveFile as FileIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
} from "@mui/icons-material";
import {
  MaintenanceRecordDto,
  MaintenanceInvoiceDto,
  useGetMaintenanceRecordsQuery,
  useDeleteMaintenanceRecordMutation,
} from "../../../api/maintenanceApi";
import {
  DataTable,
  createColumn,
  createSearchableColumn,
  Column,
  Action,
} from "../../../components/common/DataTable/DataTable";
import { SearchableColumn } from "../../../components/common/SearchInputs";

interface VehicleOdometerInfo {
  vehicleName: string;
  currentOdometer: number | null;
}

interface MaintenanceRecordsTableProps {
  vehicleNames: string[];
  vehicles?: VehicleOdometerInfo[];
  onEdit?: (record: MaintenanceRecordDto) => void;
}

const formatDate = (value: string | null | undefined): string => {
  if (!value) return "-";
  return new Date(value).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

const formatCurrency = (value: number | null | undefined): string => {
  if (value == null) return "-";
  return `$${value.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

const formatMiles = (value: number | null | undefined): string => {
  if (value == null) return "-";
  return value.toLocaleString();
};

const formatCostPerMile = (value: number | null | undefined): string => {
  if (value == null) return "-";
  return `$${value.toLocaleString(undefined, {
    minimumFractionDigits: 3,
    maximumFractionDigits: 3,
  })}/mi`;
};

const isImageType = (ct: string) => ct.startsWith("image/");

const InvoiceAvatar: React.FC<{ invoice: MaintenanceInvoiceDto }> = ({
  invoice,
}) => {
  if (isImageType(invoice.contentType)) {
    return (
      <Tooltip title={invoice.originalFileName}>
        <Avatar
          src={invoice.url}
          variant="rounded"
          sx={{ width: 32, height: 32, cursor: "pointer" }}
          onClick={() => window.open(invoice.url, "_blank")}
        />
      </Tooltip>
    );
  }
  return (
    <Tooltip title={invoice.originalFileName}>
      <Avatar
        variant="rounded"
        sx={{
          width: 32,
          height: 32,
          bgcolor: "action.hover",
          cursor: "pointer",
        }}
        onClick={() => window.open(invoice.url, "_blank")}
      >
        {invoice.contentType === "application/pdf" ? (
          <PdfIcon sx={{ fontSize: 18, color: "error.main" }} />
        ) : (
          <FileIcon sx={{ fontSize: 18, color: "text.secondary" }} />
        )}
      </Avatar>
    </Tooltip>
  );
};

const MaintenanceRecordsTable: React.FC<MaintenanceRecordsTableProps> = ({
  vehicleNames,
  vehicles = [],
  onEdit,
}) => {
  const [vehicleFilter, setVehicleFilter] = useState("");
  const [searchFilters, setSearchFilters] = useState<Record<string, any>>({});
  const { data: allRecords = [], isLoading } = useGetMaintenanceRecordsQuery();
  const [deleteRecord] = useDeleteMaintenanceRecordMutation();

  const odometerByName = useMemo(() => {
    const map = new Map<string, number | null>();
    for (const v of vehicles) {
      map.set(v.vehicleName, v.currentOdometer);
    }
    return map;
  }, [vehicles]);

  const vehicleSummary = useMemo(() => {
    if (!vehicleFilter) return null;

    const vehicleRecords = allRecords.filter(
      (r) => r.vehicleName === vehicleFilter
    );
    const totalCost = vehicleRecords.reduce(
      (sum, r) => sum + (r.cost ?? 0),
      0
    );

    // First record entered for this vehicle (by createdAt) establishes the mileage baseline
    const firstRecord = [...vehicleRecords]
      .filter((r) => r.mileage != null)
      .sort(
        (a, b) =>
          new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
      )[0];

    const currentOdometer = odometerByName.get(vehicleFilter) ?? null;
    const baselineMileage = firstRecord?.mileage ?? null;
    const milesDriven =
      currentOdometer != null && baselineMileage != null
        ? currentOdometer - baselineMileage
        : null;
    const avgCostPerMile =
      milesDriven != null && milesDriven > 0 ? totalCost / milesDriven : null;

    return {
      totalCost,
      avgCostPerMile,
      milesDriven,
      baselineMileage,
      currentOdometer,
      recordCount: vehicleRecords.length,
    };
  }, [allRecords, vehicleFilter, odometerByName]);

  const filteredRecords = useMemo(() => {
    return allRecords.filter((r) => {
      if (vehicleFilter && r.vehicleName !== vehicleFilter) return false;
      const descriptionFilter = searchFilters.description;
      if (
        typeof descriptionFilter === "string" &&
        descriptionFilter &&
        !r.description.toLowerCase().includes(descriptionFilter.toLowerCase())
      ) {
        return false;
      }
      return true;
    });
  }, [allRecords, vehicleFilter, searchFilters]);

  const handleSearchChange = useCallback(
    (filters: Record<string, any>) => setSearchFilters(filters),
    []
  );

  const searchableColumns: SearchableColumn[] = useMemo(
    () => [createSearchableColumn("description", "Description", "string")],
    []
  );

  const columns: Column[] = useMemo(
    () => [
      createColumn("vehicleName", "Vehicle", {
        flex: 1.2,
        minWidth: 150,
        renderCell: (value: string) => (
          <Typography variant="body2" sx={{ fontSize: "0.8125rem", fontWeight: 500 }}>
            {value}
          </Typography>
        ),
      }),
      createColumn("date", "Date", {
        width: 130,
        sortable: true,
        sortType: "date",
        renderCell: (value: string) => (
          <Typography variant="body2" sx={{ fontSize: "0.8125rem" }}>
            {formatDate(value)}
          </Typography>
        ),
      }),
      createColumn("description", "Description", {
        flex: 2.5,
        minWidth: 200,
        renderCell: (value: string) => (
          <Tooltip title={value}>
            <Typography
              variant="body2"
              noWrap
              sx={{ fontSize: "0.8125rem" }}
            >
              {value}
            </Typography>
          </Tooltip>
        ),
      }),
      createColumn("cost", "Cost", {
        width: 110,
        renderCell: (value: number | null) => (
          <Typography variant="body2" sx={{ fontSize: "0.8125rem", fontWeight: 500 }}>
            {formatCurrency(value)}
          </Typography>
        ),
      }),
      createColumn("mileage", "Mileage", {
        width: 110,
        renderCell: (value: number | null) => (
          <Typography variant="body2" sx={{ fontSize: "0.8125rem" }}>
            {formatMiles(value)}
          </Typography>
        ),
      }),
      createColumn("shopName", "Shop", {
        flex: 1,
        minWidth: 130,
        renderCell: (value: string | null) =>
          value ? (
            <Chip label={value} size="small" variant="outlined" sx={{ fontSize: "0.75rem" }} />
          ) : (
            <Typography variant="body2" color="text.secondary" sx={{ fontSize: "0.8125rem" }}>
              -
            </Typography>
          ),
      }),
      createColumn("invoices", "Invoices", {
        width: 120,
        renderCell: (invoices: MaintenanceInvoiceDto[]) =>
          invoices.length > 0 ? (
            <AvatarGroup max={3} sx={{ "& .MuiAvatar-root": { width: 32, height: 32 } }}>
              {invoices.map((inv) => (
                <InvoiceAvatar key={inv.id} invoice={inv} />
              ))}
            </AvatarGroup>
          ) : (
            <Typography variant="body2" color="text.secondary" sx={{ fontSize: "0.8125rem" }}>
              -
            </Typography>
          ),
      }),
    ],
    []
  );

  const toolbar = (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, px: 2, py: 1.5 }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
        <Autocomplete
          size="small"
          options={vehicleNames}
          value={vehicleFilter || null}
          onChange={(_, newValue) => setVehicleFilter(newValue ?? "")}
          renderInput={(params) => (
            <TextField {...params} label="Vehicle" placeholder="Type to search..." />
          )}
          sx={{ minWidth: 250 }}
          clearOnEscape
        />
      </Box>

      {vehicleSummary && (
        <Paper
          variant="outlined"
          sx={{
            display: "flex",
            flexWrap: "wrap",
            gap: 3,
            px: 2,
            py: 1.5,
            bgcolor: "action.hover",
          }}
        >
          <Box>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.4 }}
            >
              Total Cost
            </Typography>
            <Typography variant="h6" sx={{ fontSize: "1.1rem", fontWeight: 600, lineHeight: 1.3 }}>
              {formatCurrency(vehicleSummary.totalCost)}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {vehicleSummary.recordCount} record
              {vehicleSummary.recordCount === 1 ? "" : "s"}
            </Typography>
          </Box>

          <Box>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.4 }}
            >
              Avg Cost / Mile
            </Typography>
            <Typography variant="h6" sx={{ fontSize: "1.1rem", fontWeight: 600, lineHeight: 1.3 }}>
              {formatCostPerMile(vehicleSummary.avgCostPerMile)}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {vehicleSummary.milesDriven != null && vehicleSummary.milesDriven > 0
                ? `${formatMiles(vehicleSummary.milesDriven)} mi since first record`
                : vehicleSummary.baselineMileage == null
                  ? "Needs mileage on first record"
                  : vehicleSummary.currentOdometer == null
                    ? "Needs current odometer"
                    : "Not enough mileage yet"}
            </Typography>
          </Box>
        </Paper>
      )}
    </Box>
  );

  const handleDelete = async (record: MaintenanceRecordDto) => {
    if (!window.confirm(`Delete record "${record.description.slice(0, 50)}..."?`)) return;
    try {
      await deleteRecord(record.id).unwrap();
    } catch (err) {
      console.error("Failed to delete record:", err);
    }
  };

  const actions: Action<MaintenanceRecordDto>[] = [
    ...(onEdit
      ? [
          {
            icon: <EditIcon />,
            label: "Edit",
            onClick: (record: MaintenanceRecordDto) => onEdit(record),
          },
        ]
      : []),
    {
      icon: <DeleteIcon />,
      label: "Delete",
      onClick: handleDelete,
      color: "error" as const,
    },
  ];

  return (
    <DataTable
      items={filteredRecords}
      columns={columns}
      isLoading={isLoading}
      totalCount={filteredRecords.length}
      toolbar={toolbar}
      actions={actions}
      fitContentHeight
      enableSearch
      searchableColumns={searchableColumns}
      onSearchChange={handleSearchChange}
      enableSorting
    />
  );
};

export default MaintenanceRecordsTable;
